import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogType,
  DialogFooter,
  PrimaryButton,
  DefaultButton,
  Text,
  Icon,
  Spinner,
  SpinnerSize,
  SearchBox,
} from '@fluentui/react';
import { useFileBrowserStore } from '@/hooks';
import { BlobItem } from '@/types';
import { sharePointService } from '@/services/sharePointService';
import { logger } from '@/utils/logger';

// Folder picker for moving files/folders within the current site. Navigate
// down by clicking folders, back up via the breadcrumb; "Move here" moves the
// pending items into the folder being viewed.
export function MoveDialog() {
  const { moveDialogItems, closeMoveDialog, moveItems } = useFileBrowserStore();

  const [browsePath, setBrowsePath] = useState('');
  const [folders, setFolders] = useState<BlobItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isMoving, setIsMoving] = useState(false);
  const [filter, setFilter] = useState('');

  const isOpen = moveDialogItems !== null;

  // Reset to the root whenever the dialog opens for a new move.
  useEffect(() => {
    if (isOpen) {
      setBrowsePath('');
      setIsMoving(false);
      setFilter('');
    }
  }, [isOpen]);

  useEffect(() => {
    setFilter('');
  }, [browsePath]);

  // Load the folders of the path being browsed.
  useEffect(() => {
    if (!isOpen) return;
    let stale = false;
    setIsLoading(true);
    sharePointService
      .listItems(browsePath)
      .then((items) => {
        if (stale) return;
        // A folder being moved cannot be its own destination subtree.
        const movingFolders = (moveDialogItems || []).filter((i) => i.isFolder).map((i) => i.path);
        setFolders(items.filter((i) => i.isFolder && !movingFolders.includes(i.path)));
      })
      .catch((error) => {
        logger.warn('Move dialog: failed to list folders:', error);
        if (!stale) setFolders([]);
      })
      .finally(() => {
        if (!stale) setIsLoading(false);
      });
    return () => {
      stale = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, browsePath]);

  if (!isOpen) return null;

  const segments = browsePath.split('/').filter(Boolean);
  const itemCount = moveDialogItems?.length || 0;
  const filterLower = filter.trim().toLowerCase();
  const displayedFolders = filterLower
    ? folders.filter((folder) => folder.name.toLowerCase().includes(filterLower))
    : folders;

  const handleMove = async () => {
    if (!moveDialogItems) return;
    setIsMoving(true);
    await moveItems(moveDialogItems, browsePath);
  };

  return (
    <Dialog
      hidden={false}
      onDismiss={closeMoveDialog}
      dialogContentProps={{
        type: DialogType.normal,
        title: `Move ${itemCount} item${itemCount > 1 ? 's' : ''}`,
      }}
      modalProps={{ isBlocking: true, className: 'move-dialog' }}
    >
      {/* Breadcrumb of the folder being browsed */}
      <div className="move-breadcrumbs">
        <button type="button" className="move-crumb" onClick={() => setBrowsePath('')}>
          Root
        </button>
        {segments.map((seg, i) => (
          <span key={`${seg}-${i}`}>
            <Icon iconName="ChevronRight" className="move-crumb-sep" />
            <button
              type="button"
              className="move-crumb"
              onClick={() => setBrowsePath(segments.slice(0, i + 1).join('/') + '/')}
            >
              {seg}
            </button>
          </span>
        ))}
      </div>

      <SearchBox
        placeholder="Filter folders"
        value={filter}
        onChange={(_, value) => setFilter(value || '')}
        onClear={() => setFilter('')}
        disabled={isLoading || folders.length === 0}
        className="move-search-box"
      />

      <div className="move-folder-list">
        {isLoading ? (
          <div className="move-empty">
            <Spinner size={SpinnerSize.small} label="Loading folders" />
          </div>
        ) : folders.length === 0 ? (
          <Text className="move-empty">No subfolders</Text>
        ) : displayedFolders.length === 0 ? (
          <Text className="move-empty">No folders match &quot;{filter}&quot;</Text>
        ) : (
          displayedFolders.map((folder) => (
            <button
              key={folder.path}
              type="button"
              className="move-folder-row"
              onClick={() => setBrowsePath(folder.path)}
              title={folder.path}
            >
              <Icon iconName="FabricFolder" className="move-folder-icon" />
              <span className="move-folder-name">{folder.name}</span>
              <Icon iconName="ChevronRight" className="move-folder-chevron" />
            </button>
          ))
        )}
      </div>

      <DialogFooter>
        <PrimaryButton
          text={isMoving ? 'Moving' : 'Move here'}
          disabled={isMoving}
          onClick={handleMove}
        />
        <DefaultButton text="Cancel" disabled={isMoving} onClick={closeMoveDialog} />
      </DialogFooter>
    </Dialog>
  );
}
